"use client";
import { Component, type ErrorInfo, type ReactNode, Suspense, useEffect, useMemo, useRef } from "react";
import { TransformControls, useGLTF } from "@react-three/drei";
import { useFrame, type ThreeEvent } from "@react-three/fiber";
import { AnimationMixer, Group, LoopOnce, LoopRepeat, type AnimationAction } from "three";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { useEditorStore } from "@/stores/editorStore";
import { useSceneStore } from "@/stores/sceneStore";
import { useAnimationStore } from "@/stores/animationStore";
import { objectRegistry } from "@/runtime/objectRegistry";
import type { SceneObject } from "@/types/scene";
import { PrimitiveAsset } from "./primitives/PrimitiveAsset";
import { useTimelineStore } from "@/stores/timelineStore";
import { evaluateMovement } from "@/utils/evaluateTimeline";

function AnimatedModel({ objectId, url }: { objectId: string; url: string }) {
  const gltf = useGLTF(url);
  const model = useMemo(() => clone(gltf.scene), [gltf.scene]);
  const mixer = useMemo(() => new AnimationMixer(model), [model]);
  const actions = useRef(new Map<string, AnimationAction>());
  const playback = useAnimationStore((state) => state.byObjectId[objectId]);
  const registerAnimations = useAnimationStore((state) => state.registerAnimations);
  const setError = useAnimationStore((state) => state.setError);
  const previous = useRef<AnimationAction | null>(null);

  useEffect(() => {
    const nextActions = new Map<string, AnimationAction>();
    for (const clip of gltf.animations) nextActions.set(clip.name || `Clip ${nextActions.size + 1}`, mixer.clipAction(clip));
    actions.current = nextActions;
    registerAnimations(objectId, [...nextActions.keys()]);
    return () => {
      mixer.stopAllAction();
      mixer.uncacheRoot(model);
      actions.current.clear();
    };
  }, [gltf.animations, mixer, model, objectId, registerAnimations]);

  useEffect(() => {
    if (!playback?.playing || !playback.active) { previous.current?.fadeOut(.2); previous.current = null; return; }
    const next = actions.current.get(playback.active);
    if (!next) { setError(objectId, `Animation “${playback.active}” was not found.`); return; }
    const playOnce = /fall|jump|reaction/i.test(playback.active);
    next.reset().setLoop(playOnce ? LoopOnce : LoopRepeat, playOnce ? 1 : Infinity).setEffectiveWeight(1).fadeIn(.25).play();
    if (playOnce) next.clampWhenFinished = true;
    if (previous.current && previous.current !== next) previous.current.crossFadeTo(next, .25, false);
    previous.current = next;
  }, [objectId, playback?.active, playback?.playing, setError]);

  useFrame((_, delta) => mixer.update(delta));
  return <primitive object={model} />;
}

class ModelErrorBoundary extends Component<{ children: ReactNode; onError: (message: string) => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) { void info; this.props.onError(error.message || "Model could not be loaded."); }
  render() { return this.state.failed ? <mesh castShadow position-y={.5}><boxGeometry args={[1, 1, 1]} /><meshStandardMaterial color="#9b3d35" /></mesh> : this.props.children; }
}

export function SceneObject3D({ object }: { object: SceneObject }) {
  const ref = useRef<Group>(null);
  const selectedId = useEditorStore((state) => state.selectedObjectId);
  const selectObject = useEditorStore((state) => state.selectObject);
  const activeTool = useEditorStore((state) => state.activeTool);
  const fullscreenPreview = useEditorStore((state) => state.fullscreenPreview);
  const walkToActorId = useEditorStore((state) => state.walkToActorId);
  const walkDuration = useEditorStore((state) => state.walkDuration);
  const stoppingDistance = useEditorStore((state) => state.stoppingDistance);
  const autoFaceDirection = useEditorStore((state) => state.autoFaceDirection);
  const cancelWalkTo = useEditorStore((state) => state.cancelWalkTo);
  const assetAnimations = useSceneStore((state) => state.assets.find((asset) => asset.id === object.assetId)?.animations);
  const updateTransform = useSceneStore((state) => state.updateTransform);
  const beginTransform = useSceneStore((state) => state.beginTransform);
  const commitTransform = useSceneStore((state) => state.commitTransform);
  const registerAnimations = useAnimationStore((state) => state.registerAnimations);
  const setLoading = useAnimationStore((state) => state.setLoading);
  const setError = useAnimationStore((state) => state.setError);
  const removeRuntime = useAnimationStore((state) => state.removeRuntime);
  const addWalkToAction = useTimelineStore((state) => state.addWalkToAction);
  const selected = selectedId === object.id;
  useEffect(() => {
    if (object.primitive && assetAnimations) registerAnimations(object.id, assetAnimations);
    if (object.modelUrl && !object.primitive) setLoading(object.id, true);
    return () => { objectRegistry.delete(object.id); removeRuntime(object.id); };
  }, [assetAnimations, object.id, object.modelUrl, object.primitive, registerAnimations, removeRuntime, setLoading]);
  useEffect(() => {
    if (!ref.current) return;
    objectRegistry.set(object.id, ref.current);
    ref.current.position.set(object.transform.position.x, object.transform.position.y, object.transform.position.z);
    ref.current.rotation.set(object.transform.rotation.x, object.transform.rotation.y, object.transform.rotation.z);
    ref.current.scale.set(object.transform.scale.x, object.transform.scale.y, object.transform.scale.z);
  }, [object.id, object.transform]);
  const sync = () => {
    const group = ref.current;
    if (!group) return;
    updateTransform(object.id, { position: { x: group.position.x, y: group.position.y, z: group.position.z }, rotation: { x: group.rotation.x, y: group.rotation.y, z: group.rotation.z }, scale: { x: group.scale.x, y: group.scale.y, z: group.scale.z } }, false);
  };
  const pick = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    if (walkToActorId && walkToActorId !== object.id) {
      const actor = useSceneStore.getState().objects.find((item) => item.id === walkToActorId);
      if (actor) {
        const timelineState = useTimelineStore.getState();
        const evaluatedStart = evaluateMovement(timelineState.tracks, actor.id, timelineState.currentTime)?.position ?? actor.transform.position;
        const targetPoint = { x: object.transform.position.x + (object.interactionPoint?.x ?? 0), y: object.transform.position.y + (object.interactionPoint?.y ?? 0), z: object.transform.position.z + (object.interactionPoint?.z ?? 0) };
        const dx = targetPoint.x - evaluatedStart.x;
        const dz = targetPoint.z - evaluatedStart.z;
        const distance = Math.hypot(dx, dz);
        const stop = Math.min(stoppingDistance, Math.max(0, distance - .05));
        const to = distance > .001 ? { x: targetPoint.x - dx / distance * stop, y: targetPoint.y, z: targetPoint.z - dz / distance * stop } : targetPoint;
        addWalkToAction(actor.id, object.id, { ...evaluatedStart }, to, walkDuration, autoFaceDirection, stoppingDistance);
        cancelWalkTo();
        selectObject(actor.id);
      }
      return;
    }
    selectObject(object.id);
  };
  const content = <PrimitiveAsset objectId={object.id} kind={object.primitive} selected={selected && !fullscreenPreview}>{object.modelUrl && !object.primitive ? <ModelErrorBoundary onError={(message) => setError(object.id, message)}><Suspense fallback={null}><AnimatedModel objectId={object.id} url={object.modelUrl} /></Suspense></ModelErrorBoundary> : null}</PrimitiveAsset>;
  const group = <group ref={ref} onPointerDown={pick} visible={object.visible && !(fullscreenPreview && object.type === "camera")}>{content}</group>;
  return selected && activeTool !== "select" && !fullscreenPreview ? <TransformControls mode={activeTool} onMouseDown={beginTransform} onMouseUp={commitTransform} onObjectChange={sync}>{group}</TransformControls> : group;
}
